import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { errorText, type NotificationEvent } from '@/lib/portal';

export const Route = createFileRoute('/settings/notifications')({ component: Notifications });

interface Prefs {
  delivers: boolean;
  preferences: Array<{ event: NotificationEvent; email: boolean }>;
}

/**
 * Notification preferences (gear › Profile › Notifications): which events also reach the
 * signed-in user by email, for the current client. Notifications always show in the top bar.
 */
function Notifications() {
  const t = useT();
  const label = (e: NotificationEvent) => ({
    title: t(`settingsPages.company.notifications.events.${e}.title`),
    hint: t(`settingsPages.company.notifications.events.${e}.hint`),
  });
  const orgId = useOrgId();
  const qc = useQueryClient();
  const key = ['portal', orgId, 'preferences'];
  const prefs = useQuery({
    queryKey: key,
    queryFn: () => api<Prefs>(`/orgs/${orgId}/notifications/preferences`),
    enabled: Boolean(orgId),
  });
  const save = useMutation({
    mutationFn: (p: { event: NotificationEvent; email: boolean }) =>
      api<Prefs>(`/orgs/${orgId}/notifications/preferences`, {
        method: 'PUT',
        body: JSON.stringify({ preferences: [p] }),
      }),
    onSuccess: (r, p) => {
      qc.setQueryData(key, r);
      toast(
        t(
          p.email
            ? 'settingsPages.company.notifications.emailOn'
            : 'settingsPages.company.notifications.emailOff',
          { event: label(p.event).title },
        ),
      );
    },
    onError: (e) => toast(errorText(e), { tone: 'error' }),
  });
  if (!orgId)
    return (
      <EmptyState
        title={t('shell.selectClient')}
        hint={t('settingsPages.company.page.selectClientHint')}
      />
    );
  return (
    <>
      <PageHeader
        title={t('settingsPages.company.notifications.title')}
        description={
          prefs.data && !prefs.data.delivers
            ? t('settingsPages.company.notifications.descriptionNoDelivery')
            : t('settingsPages.company.notifications.description')
        }
      />
      {prefs.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : prefs.isError ? (
        <EmptyState
          title={t('settingsPages.company.notifications.loadError')}
          hint={prefs.error.message}
        />
      ) : !prefs.data.preferences.length ? (
        <EmptyState
          title={t('settingsPages.company.notifications.nothing')}
          hint={t('settingsPages.company.notifications.nothingHint')}
        />
      ) : (
        <section
          className="max-w-2xl rounded-lg border bg-card"
          data-testid="notification-preferences"
        >
          <div className="grid grid-cols-[1fr_80px] gap-2 border-b px-5 py-3 text-sm font-medium text-muted-foreground">
            <span>{t('settingsPages.company.notifications.event')}</span>
            <span className="text-center">{t('settingsPages.company.notifications.email')}</span>
          </div>
          <ul className="divide-y">
            {prefs.data.preferences.map((p) => (
              <li key={p.event} className="grid grid-cols-[1fr_80px] items-center gap-2 px-5 py-3">
                <div>
                  <div className="text-sm font-medium">{label(p.event).title}</div>
                  <div className="text-xs text-muted-foreground">{label(p.event).hint}</div>
                </div>
                <div className="text-center">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    aria-label={t('settingsPages.company.notifications.emailAria', {
                      event: label(p.event).title,
                    })}
                    checked={p.email}
                    disabled={save.isPending}
                    onChange={(e) => save.mutate({ event: p.event, email: e.target.checked })}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
